import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, LogOut, Lock, Save, CheckCircle2, Star, Eye, EyeOff, Mail, Send, History, BarChart3 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUALIFICATION_EVENTS = [
  { value: "signup", label: "Referred user signs up" },
  { value: "first_inspection", label: "Referred user logs first inspection" },
  { value: "first_harvest_log", label: "Referred user logs first harvest" },
  { value: "subscription_start", label: "Referred user starts a subscription" },
];

const TEMPLATE_LABELS = { one_week: "One Week to Launch", launch_day: "We're Live", beta_invite: "Beta Tester Invite" };

const fieldCls = "bg-white border-forest/15 focus-visible:ring-honey focus-visible:ring-2 focus-visible:ring-offset-0 rounded-xl h-11";

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white border border-forest/10 rounded-2xl p-6" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.2em] text-ink/40 mb-2">{label}</p>
    <p className="font-display text-4xl font-medium text-forest">{value}</p>
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("ak_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("signups");
  const [signups, setSignups] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [blastPreview, setBlastPreview] = useState(null);
  const [blastTemplate, setBlastTemplate] = useState("one_week");
  const [betaLink, setBetaLink] = useState("");
  const [blasting, setBlasting] = useState(false);
  const [blastResult, setBlastResult] = useState(null);
  const [blastHistory, setBlastHistory] = useState(null);

  const authed = useCallback(
    (config = {}) => ({ ...config, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const expire = useCallback(() => {
    sessionStorage.removeItem("ak_admin_token");
    setToken("");
    toast.error("Session expired. Please sign in again.");
  }, []);

  const fetchSignups = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/signups`, authed());
      setSignups(data.signups);
    } catch { expire(); }
  }, [authed, expire]);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, authed());
      setSettings(data);
    } catch { expire(); }
  }, [authed, expire]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/referrals`, authed());
      setAnalytics(data);
    } catch { expire(); }
  }, [authed, expire]);

  const fetchBlastData = useCallback(async () => {
    try {
      const [preview, history] = await Promise.all([
        axios.get(`${API}/admin/blast/preview`, authed()),
        axios.get(`${API}/admin/blasts`, authed()),
      ]);
      setBlastPreview(preview.data);
      setBlastHistory(history.data.blasts);
    } catch { expire(); }
  }, [authed, expire]);

  useEffect(() => {
    if (!token) return;
    if (tab === "signups") fetchSignups();
    if (tab === "settings") fetchSettings();
    if (tab === "analytics") fetchAnalytics();
    if (tab === "blast") fetchBlastData();
  }, [token, tab, fetchSignups, fetchSettings, fetchAnalytics, fetchBlastData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      sessionStorage.setItem("ak_admin_token", data.token);
      setToken(data.token);
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("ak_admin_token");
    setToken("");
  };

  const exportCsv = () => {
    if (!signups?.length) return;
    const cols = ["first_name", "email", "hive_count", "record_method", "interests", "biggest_challenge", "beta_interest", "heard_from", "source", "referred", "referral_name", "referral_email", "referral_code", "referred_by_code", "own_referral_code", "founding_keeper", "hall_listed", "tips_series", "unsubscribed", "marketing_consent", "created_at"];
    const esc = (v) => `"${String(Array.isArray(v) ? v.join("; ") : v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...signups.map((s) => cols.map((c) => esc(s[c])).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `apiarykeeper-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleFounding = async (s) => {
    try {
      await axios.put(`${API}/admin/signups/founding`, { email: s.email, founding_keeper: !s.founding_keeper, hall_listed: true }, authed());
      toast.success(s.founding_keeper ? `${s.first_name} removed from the Hall.` : `${s.first_name} added to the Founding Keepers Hall.`);
      fetchSignups();
    } catch {
      toast.error("Couldn't update Founding Keeper status.");
    }
  };

  const toggleHall = async (s) => {
    try {
      await axios.put(`${API}/admin/signups/hall`, { email: s.email, hall_listed: !s.hall_listed }, authed());
      toast.success(s.hall_listed ? `${s.first_name} hidden from the Hall.` : `${s.first_name} shown in the Hall.`);
      fetchSignups();
    } catch {
      toast.error("Couldn't update Hall visibility.");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        founding_total: Number(settings.founding_total),
        founding_claimed: Number(settings.founding_claimed),
        launch_date: settings.launch_date || null,
        referral: {
          enabled: !!settings.referral.enabled,
          reward_type: settings.referral.reward_type,
          reward_amount: Number(settings.referral.reward_amount),
          qualification_event: settings.referral.qualification_event,
          max_rewards_per_user: Number(settings.referral.max_rewards_per_user),
          campaign_start: settings.referral.campaign_start || null,
          campaign_end: settings.referral.campaign_end || null,
        },
      };
      const { data } = await axios.put(`${API}/admin/settings`, payload, authed());
      setSettings(data);
      toast.success("Settings saved.");
    } catch {
      toast.error("Couldn't save settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const markQualified = async (signupEmail) => {
    try {
      await axios.put(`${API}/admin/signups/referral-status`, { email: signupEmail, status: "qualified" }, authed());
      toast.success("Referral marked qualified.");
      fetchAnalytics();
    } catch {
      toast.error("Couldn't update referral.");
    }
  };

  const sendBlast = async () => {
    if (!blastPreview) return;
    const isBeta = blastTemplate === "beta_invite";
    if (isBeta && !betaLink.trim().toLowerCase().startsWith("https://")) {
      toast.error("Paste the https beta link first (TestFlight or Google Play).");
      return;
    }
    const count = isBeta ? blastPreview.beta_eligible : blastPreview.eligible;
    if (!window.confirm(`Send "${TEMPLATE_LABELS[blastTemplate]}" to ${count} beekeepers?`)) return;
    setBlasting(true);
    setBlastResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/blast`, { template: blastTemplate, beta_link: isBeta ? betaLink.trim() : null }, authed());
      setBlastResult(data);
      toast.success(`Sent to ${data.sent} of ${data.total}.`);
      fetchBlastData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blast failed. Check the logs.");
    } finally {
      setBlasting(false);
    }
  };

  if (!token) {
    return (
      <section className="max-w-md mx-auto px-6 py-28 md:py-36" data-testid="admin-login-section">
        <div className="bg-white border border-forest/10 rounded-3xl p-10">
          <Lock className="w-8 h-8 text-honey-dark mb-6" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-medium tracking-tight text-forest mb-2">Admin</h1>
          <p className="text-sm text-ink/50 mb-8">Launch list signups — authorized access only.</p>
          <form onSubmit={handleLogin} className="flex flex-col gap-5" data-testid="admin-login-form">
            <div className="flex flex-col gap-2">
              <Label htmlFor="admin_email">Email</Label>
              <Input id="admin_email" type="email" data-testid="admin-email-input" className={fieldCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="admin_password">Password</Label>
              <Input id="admin_password" type="password" data-testid="admin-password-input" className={fieldCls} value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-button"
              className="inline-flex items-center justify-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  const tabs = [
    { id: "signups", label: "Signups" },
    { id: "settings", label: "Founding & Referral Settings" },
    { id: "analytics", label: "Referral Analytics" },
    { id: "blast", label: "Email Blast" },
  ];

  const blastCount = blastPreview
    ? blastTemplate === "beta_invite" ? blastPreview.beta_eligible : blastPreview.eligible
    : 0;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pt-4">
        <h1 className="font-display text-4xl font-medium tracking-tight text-forest">Admin</h1>
        <button onClick={logout} data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 text-sm text-ink/50 px-5 py-2.5 rounded-full hover:text-forest transition-colors duration-300">
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-300 ${
              tab === t.id ? "bg-forest text-bone" : "bg-white border border-forest/15 text-forest/70 hover:border-honey"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "signups" && (
        <div data-testid="admin-signups-panel">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="text-sm text-ink/50" data-testid="admin-signup-count">
              {signups ? `${signups.length} total signups` : "Loading..."}
            </p>
            <button onClick={exportCsv} data-testid="admin-export-button"
              className="inline-flex items-center gap-2 border border-forest/20 text-forest text-sm font-medium px-5 py-2.5 rounded-full hover:border-honey transition-colors duration-300">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
          {!signups ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-honey-dark" /></div>
          ) : signups.length === 0 ? (
            <p className="text-ink/50 py-20 text-center" data-testid="admin-empty-state">No signups yet.</p>
          ) : (
            <div className="overflow-x-auto border border-forest/10 rounded-2xl bg-white" data-testid="admin-signups-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-left text-xs uppercase tracking-wider text-ink/50">
                    <th className="px-5 py-4">Name</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Hives</th>
                    <th className="px-5 py-4">Source</th>
                    <th className="px-5 py-4">Beta</th>
                    <th className="px-5 py-4">Referral Code</th>
                    <th className="px-5 py-4">Referred By</th>
                    <th className="px-5 py-4">Founding</th>
                    <th className="px-5 py-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s) => (
                    <tr key={s.email} className="border-b border-forest/5 last:border-0 hover:bg-honey/5 transition-colors duration-200" data-testid={`admin-signup-row-${s.email}`}>
                      <td className="px-5 py-4 font-medium text-forest">{s.first_name}</td>
                      <td className="px-5 py-4 text-ink/70">{s.email}</td>
                      <td className="px-5 py-4 text-ink/70">{s.hive_count}</td>
                      <td className="px-5 py-4 text-ink/70">{s.source || "direct"}</td>
                      <td className="px-5 py-4">{s.beta_interest ? "Yes" : "—"}</td>
                      <td className="px-5 py-4 font-mono text-xs">{s.own_referral_code || "—"}</td>
                      <td className="px-5 py-4">{s.referred_by_code || (s.referred ? "Yes (unmatched)" : "—")}</td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1">
                          <button
                            onClick={() => toggleFounding(s)}
                            data-testid={`founding-toggle-${s.email}`}
                            title={s.founding_keeper ? "Remove from Founding Keepers" : "Make Founding Keeper"}
                            className="p-1.5 rounded-full hover:bg-honey/15 transition-colors duration-200"
                          >
                            <Star className={`w-4 h-4 ${s.founding_keeper ? "fill-honey text-honey-dark" : "text-ink/30"}`} />
                          </button>
                          {s.founding_keeper && (
                            <button
                              onClick={() => toggleHall(s)}
                              data-testid={`hall-toggle-${s.email}`}
                              title={s.hall_listed ? "Hide name from the Hall" : "Show name in the Hall"}
                              className="p-1.5 rounded-full hover:bg-honey/15 transition-colors duration-200"
                            >
                              {s.hall_listed
                                ? <Eye className="w-4 h-4 text-forest/70" />
                                : <EyeOff className="w-4 h-4 text-ink/30" />}
                            </button>
                          )}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-ink/50 whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div data-testid="admin-settings-panel">
          {!settings ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-honey-dark" /></div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-white border border-forest/10 rounded-3xl p-8" data-testid="founding-settings-card">
                <h2 className="font-display text-2xl text-forest mb-2">Founding Keeper Counter</h2>
                <p className="text-sm text-ink/50 mb-6">Controls the "X of 100 spots remaining" counter shown site-wide. Marking Founding Keepers in the Signups tab updates "claimed" automatically.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="founding_total">Total spots</Label>
                    <Input id="founding_total" type="number" min="0" data-testid="input-founding-total" className={fieldCls}
                      value={settings.founding_total}
                      onChange={(e) => setSettings({ ...settings, founding_total: e.target.value })} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="founding_claimed">Spots claimed</Label>
                    <Input id="founding_claimed" type="number" min="0" data-testid="input-founding-claimed" className={fieldCls}
                      value={settings.founding_claimed}
                      onChange={(e) => setSettings({ ...settings, founding_claimed: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-forest/10 rounded-3xl p-8" data-testid="launch-date-card">
                <h2 className="font-display text-2xl text-forest mb-2">Launch Date</h2>
                <p className="text-sm text-ink/50 mb-6">When set, a countdown timer appears in the homepage hero and in welcome emails. Clear it to hide the countdown.</p>
                <div className="flex items-end gap-3">
                  <div className="flex flex-col gap-2 flex-1">
                    <Label htmlFor="launch_date">Full launch date</Label>
                    <Input id="launch_date" type="date" data-testid="input-launch-date" className={fieldCls}
                      value={settings.launch_date || ""}
                      onChange={(e) => setSettings({ ...settings, launch_date: e.target.value })} />
                  </div>
                  {settings.launch_date && (
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, launch_date: null })}
                      data-testid="clear-launch-date-button"
                      className="h-11 px-4 text-sm text-ink/50 border border-forest/15 rounded-xl hover:border-honey hover:text-forest transition-colors duration-300"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="bg-white border border-forest/10 rounded-3xl p-8 lg:col-span-2" data-testid="referral-settings-card">
                <h2 className="font-display text-2xl text-forest mb-6">Referral Program</h2>
                <div className="flex flex-col gap-5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox data-testid="input-referral-enabled" checked={settings.referral.enabled}
                      onCheckedChange={(v) => setSettings({ ...settings, referral: { ...settings.referral, enabled: !!v } })}
                      className="border-forest/30 data-[state=checked]:bg-honey data-[state=checked]:border-honey data-[state=checked]:text-forest" />
                    <span className="text-sm text-ink/80">Referral program active</span>
                  </label>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <Label>Reward type</Label>
                      <Input data-testid="input-reward-type" className={fieldCls} value={settings.referral.reward_type}
                        onChange={(e) => setSettings({ ...settings, referral: { ...settings.referral, reward_type: e.target.value } })} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label>Reward amount</Label>
                      <Input type="number" min="0" data-testid="input-reward-amount" className={fieldCls} value={settings.referral.reward_amount}
                        onChange={(e) => setSettings({ ...settings, referral: { ...settings.referral, reward_amount: e.target.value } })} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Qualification event</Label>
                    <Select value={settings.referral.qualification_event}
                      onValueChange={(v) => setSettings({ ...settings, referral: { ...settings.referral, qualification_event: v } })}>
                      <SelectTrigger data-testid="input-qualification-event" className={fieldCls}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUALIFICATION_EVENTS.map((q) => (
                          <SelectItem key={q.value} value={q.value} data-testid={`qualification-option-${q.value}`}>{q.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-2">
                      <Label>Max rewards / user</Label>
                      <Input type="number" min="0" data-testid="input-max-rewards" className={fieldCls} value={settings.referral.max_rewards_per_user}
                        onChange={(e) => setSettings({ ...settings, referral: { ...settings.referral, max_rewards_per_user: e.target.value } })} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label>Campaign start</Label>
                      <Input type="date" data-testid="input-campaign-start" className={fieldCls} value={settings.referral.campaign_start || ""}
                        onChange={(e) => setSettings({ ...settings, referral: { ...settings.referral, campaign_start: e.target.value } })} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label>Campaign end</Label>
                      <Input type="date" data-testid="input-campaign-end" className={fieldCls} value={settings.referral.campaign_end || ""}
                        onChange={(e) => setSettings({ ...settings, referral: { ...settings.referral, campaign_end: e.target.value } })} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-button"
                  className="inline-flex items-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest disabled:opacity-60">
                  {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "analytics" && (
        <div data-testid="admin-analytics-panel">
          {!analytics ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-honey-dark" /></div>
          ) : (
            <div className="flex flex-col gap-10">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="analytics-stats">
                <StatCard label="Total signups" value={analytics.total_registrations} testid="stat-total-registrations" />
                <StatCard label="Referred" value={analytics.referred_signups} testid="stat-referred-signups" />
                <StatCard label="Qualified" value={analytics.qualified_referrals} testid="stat-qualified-referrals" />
                <StatCard label="Pending" value={analytics.pending_referrals} testid="stat-pending-referrals" />
                <StatCard label="Rewards issued" value={analytics.rewards_issued} testid="stat-rewards-issued" />
                <StatCard label="Conversion" value={`${analytics.conversion_rate}%`} testid="stat-conversion-rate" />
              </div>

              <div className="grid lg:grid-cols-3 gap-8">
                <div className="bg-white border border-forest/10 rounded-3xl p-8" data-testid="sources-card">
                  <h2 className="font-display text-2xl text-forest mb-2 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-honey-dark" /> Signups by Source
                  </h2>
                  <p className="text-xs text-ink/40 mb-6">Which sections send beekeepers to the list.</p>
                  {analytics.sources.length === 0 ? (
                    <p className="text-sm text-ink/50">No signups yet.</p>
                  ) : (
                    <div className="flex flex-col gap-4" data-testid="sources-chart">
                      {analytics.sources.map((s) => {
                        const max = analytics.sources[0].count;
                        const pct = Math.max((s.count / max) * 100, 6);
                        return (
                          <div key={s.source} data-testid={`source-row-${s.source}`}>
                            <div className="flex justify-between text-sm mb-1.5">
                              <span className="font-medium text-forest">{s.source}</span>
                              <span className="text-ink/50">{s.count}</span>
                            </div>
                            <div className="h-2 bg-forest/5 rounded-full overflow-hidden">
                              <div className="h-full bg-honey rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-white border border-forest/10 rounded-3xl p-8" data-testid="top-referrers-card">
                  <h2 className="font-display text-2xl text-forest mb-6">Top Referrers</h2>
                  {analytics.top_referrers.length === 0 ? (
                    <p className="text-sm text-ink/50">No referrals yet.</p>
                  ) : (
                    <div className="flex flex-col divide-y divide-forest/10">
                      {analytics.top_referrers.map((r) => (
                        <div key={r.code} className="flex items-center justify-between py-3.5" data-testid={`top-referrer-${r.code}`}>
                          <div>
                            <p className="text-sm font-medium text-forest">{r.name} <span className="text-ink/40 font-normal">· {r.email}</span></p>
                            <p className="text-xs text-ink/40 font-mono">{r.code}</p>
                          </div>
                          <p className="text-sm text-ink/60">{r.referrals} joined · {r.qualified} qualified</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white border border-forest/10 rounded-3xl p-8" data-testid="pending-referrals-card">
                  <h2 className="font-display text-2xl text-forest mb-6">Pending Referrals</h2>
                  {analytics.pending_list.length === 0 ? (
                    <p className="text-sm text-ink/50">Nothing pending.</p>
                  ) : (
                    <div className="flex flex-col divide-y divide-forest/10">
                      {analytics.pending_list.map((p) => (
                        <div key={p.email} className="flex items-center justify-between gap-4 py-3.5" data-testid={`pending-referral-${p.email}`}>
                          <div>
                            <p className="text-sm font-medium text-forest">{p.first_name} <span className="text-ink/40 font-normal">· {p.email}</span></p>
                            <p className="text-xs text-ink/40">via code <span className="font-mono">{p.referred_by_code}</span></p>
                          </div>
                          <button onClick={() => markQualified(p.email)} data-testid={`mark-qualified-${p.email}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest border border-forest/20 px-4 py-2 rounded-full hover:border-honey hover:bg-honey/10 transition-colors duration-300">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Mark Qualified
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "blast" && (
        <div data-testid="admin-blast-panel" className="max-w-2xl flex flex-col gap-8">
          {!blastPreview ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-honey-dark" /></div>
          ) : (
            <>
              <div className="bg-white border border-forest/10 rounded-3xl p-8 md:p-10" data-testid="blast-card">
                <Mail className="w-8 h-8 text-honey-dark mb-5" strokeWidth={1.5} />
                <h2 className="font-display text-2xl text-forest mb-2">Email the Launch List</h2>
                <p className="text-sm text-ink/50 mb-8">
                  Marketing emails go to the{" "}
                  <span className="font-semibold text-forest" data-testid="blast-eligible-count">{blastPreview.eligible} beekeepers</span>{" "}
                  who opted in and haven't unsubscribed. Beta invites go to the{" "}
                  <span className="font-semibold text-forest" data-testid="blast-beta-count">{blastPreview.beta_eligible} beekeepers</span>{" "}
                  who raised a hand for testing.
                </p>

                <Label className="text-forest font-medium mb-3 block">Choose the email</Label>
                <RadioGroup value={blastTemplate} onValueChange={setBlastTemplate} className="flex flex-col gap-3 mb-6" data-testid="blast-template-group">
                  <label className={`flex items-start gap-3 border rounded-xl px-4 py-4 cursor-pointer transition-colors duration-300 ${blastTemplate === "one_week" ? "border-honey bg-honey/10" : "border-forest/15 hover:border-honey/60"}`}>
                    <RadioGroupItem value="one_week" data-testid="blast-template-one-week" className="mt-0.5 border-forest/30 text-honey-dark" />
                    <span>
                      <span className="block text-sm font-medium text-forest">One Week to Launch</span>
                      <span className="block text-xs text-ink/50 mt-1">
                        Countdown excitement + referral push
                        {blastPreview.launch_date ? ` (launch date: ${blastPreview.launch_date})` : " (uses \"very soon\" — set a launch date first)"}
                      </span>
                    </span>
                  </label>
                  <label className={`flex items-start gap-3 border rounded-xl px-4 py-4 cursor-pointer transition-colors duration-300 ${blastTemplate === "launch_day" ? "border-honey bg-honey/10" : "border-forest/15 hover:border-honey/60"}`}>
                    <RadioGroupItem value="launch_day" data-testid="blast-template-launch-day" className="mt-0.5 border-forest/30 text-honey-dark" />
                    <span>
                      <span className="block text-sm font-medium text-forest">We're Live</span>
                      <span className="block text-xs text-ink/50 mt-1">Launch day announcement with download instructions</span>
                    </span>
                  </label>
                  <label className={`flex items-start gap-3 border rounded-xl px-4 py-4 cursor-pointer transition-colors duration-300 ${blastTemplate === "beta_invite" ? "border-honey bg-honey/10" : "border-forest/15 hover:border-honey/60"}`}>
                    <RadioGroupItem value="beta_invite" data-testid="blast-template-beta-invite" className="mt-0.5 border-forest/30 text-honey-dark" />
                    <span>
                      <span className="block text-sm font-medium text-forest">Beta Tester Invite</span>
                      <span className="block text-xs text-ink/50 mt-1">Sends your beta download link to everyone interested in beta testing</span>
                    </span>
                  </label>
                </RadioGroup>

                {blastTemplate === "beta_invite" && (
                  <div className="flex flex-col gap-2 mb-6" data-testid="beta-link-field">
                    <Label htmlFor="beta_link">Beta download link (TestFlight or Google Play, https)</Label>
                    <Input
                      id="beta_link"
                      data-testid="input-beta-link"
                      className={fieldCls}
                      placeholder="https://testflight.apple.com/join/…"
                      value={betaLink}
                      onChange={(e) => setBetaLink(e.target.value)}
                    />
                  </div>
                )}

                <button
                  onClick={sendBlast}
                  disabled={blasting || blastCount === 0}
                  data-testid="send-blast-button"
                  className="inline-flex items-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest disabled:opacity-60"
                >
                  {blasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {blasting ? "Sending..." : `Send to ${blastCount} beekeepers`}
                </button>

                {blastResult && (
                  <div className="mt-6 bg-honey/10 border border-honey/50 rounded-xl px-5 py-4 text-sm text-forest" data-testid="blast-result">
                    Sent <b>{blastResult.sent}</b> of {blastResult.total}
                    {blastResult.failed > 0 && <span className="text-ink/50"> · {blastResult.failed} failed (see backend logs)</span>}
                  </div>
                )}
              </div>

              <div className="bg-white border border-forest/10 rounded-3xl p-8 md:p-10" data-testid="blast-history-card">
                <h2 className="font-display text-2xl text-forest mb-6 flex items-center gap-2">
                  <History className="w-5 h-5 text-honey-dark" /> Blast History
                </h2>
                {!blastHistory || blastHistory.length === 0 ? (
                  <p className="text-sm text-ink/50" data-testid="blast-history-empty">No blasts sent yet.</p>
                ) : (
                  <div className="flex flex-col divide-y divide-forest/10" data-testid="blast-history-list">
                    {blastHistory.map((b, i) => (
                      <div key={`${b.sent_at}-${i}`} className="flex items-center justify-between gap-4 py-3.5" data-testid={`blast-history-row-${i}`}>
                        <div>
                          <p className="text-sm font-medium text-forest">{TEMPLATE_LABELS[b.template] || b.template}</p>
                          <p className="text-xs text-ink/40">{new Date(b.sent_at).toLocaleString()}</p>
                        </div>
                        <p className="text-sm text-ink/60 whitespace-nowrap">
                          {b.sent}/{b.total} sent{b.failed > 0 ? ` · ${b.failed} failed` : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
